import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { appConfig } from '../config';
import { t } from 'i18next';

// translation keys in `httpErrors`
const httpErrorMessages = {
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  500: 'internalServerError',
};

const detailMessageOverrides: Record<string, string> = {
  lca_broken_sub_product_reference: 'lca:errors.brokenSubProductReference',
};

export interface NinjaValidationData {
  _toast?: string;
  [key: string]: string | undefined;
}
export class NinjaValidationError extends AxiosError {
  errors: NinjaValidationData;
  constructor(message: string, errors: NinjaValidationData) {
    super(message);
    this.errors = errors;
  }
}

const parseBlobJson = async (data: unknown) => {
  if (!(data instanceof Blob) || !data.type.includes('application/json')) {
    return data;
  }

  const text =
    typeof data.text === 'function'
      ? await data.text()
      : await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(reader.error);
          reader.readAsText(data);
        });

  return JSON.parse(text);
};

export const handleToastError = async (error: any) => {
  if ([400, 403, 422, 429].includes(error?.response?.status)) {
    const data = await parseBlobJson(error?.response?.data);
    const detail = data?.detail;
    if (typeof detail === 'string') {
      const translationKey = detailMessageOverrides[detail];
      if (translationKey) {
        toast.error(t(translationKey));
        return;
      }
      toast.error(detail);
      return;
    }

    for (const { msg } of data?.errors || []) toast.error(String(msg));
  }
  const errorMessage = (httpErrorMessages as any)[error?.response?.status];
  if (errorMessage) toast.error(t(`httpErrors.${errorMessage}`));
};

const buildClient = (baseUrl: string): AxiosInstance => {
  const config: AxiosRequestConfig = {
    baseURL: baseUrl,
    validateStatus: (status) => status >= 200 && status < 300,
    withCredentials: false,
    paramsSerializer: {
      indexes: null,
    },
  };
  const client = axios.create(config);
  return client;
};

// Axios client that performs authenticated requests
export const client = buildClient(appConfig.backendApiUrl);

// Axios client that performs anonymous requests without auth
export const anonymousClient = buildClient(appConfig.backendApiUrl);
